<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useConvexClient } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import { MULTIPLAYER_ARCHITECTURE } from '@/config/multiplayer';

type BrowserConnectionInfo = {
  rtt?: number;
  downlink?: number;
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g' | string;
  addEventListener?: (type: 'change', listener: EventListener) => void;
  removeEventListener?: (type: 'change', listener: EventListener) => void;
};

const isLanMode = MULTIPLAYER_ARCHITECTURE === 'lan-p2p';

const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true);
const latencyMs = ref<number | null>(null);
const wifiLevel = ref(2);
const isBackendReachable = ref(true);
let pingTimer: ReturnType<typeof setInterval> | null = null;
let isMeasuring = false;

const convexClient = useConvexClient();

const connectionInfo = () => {
  return (navigator as Navigator & { connection?: BrowserConnectionInfo }).connection;
};

const deriveWifiLevel = () => {
  if (!isOnline.value) return 0;

  const info = connectionInfo();
  if (!info) return 2;

  let score = 2;

  if (info.effectiveType === '4g') score = 3;
  if (info.effectiveType === '3g') score = Math.min(score, 2);
  if (info.effectiveType === '2g' || info.effectiveType === 'slow-2g') score = 1;

  if (typeof info.downlink === 'number') {
    if (info.downlink >= 15) score = 3;
    else if (info.downlink < 2) score = Math.min(score, 1);
  }

  if (typeof info.rtt === 'number' && info.rtt > 0) {
    if (info.rtt <= 80) score = Math.max(score, 3);
    else if (info.rtt > 240) score = Math.min(score, 1);
  }

  return Math.max(1, Math.min(3, score));
};

const level = computed(() => {
  if (!isOnline.value) return 0;

  if (isLanMode) {
    return wifiLevel.value;
  }

  if (!isBackendReachable.value) return 1;
  if (latencyMs.value == null) return 2;
  if (latencyMs.value <= 90) return 3;
  if (latencyMs.value <= 180) return 2;
  return 1;
});

const qualityClass = computed(() => {
  if (!isOnline.value) return 'text-error';

  if (isLanMode) {
    if (wifiLevel.value >= 3) return 'text-success';
    if (wifiLevel.value >= 2) return 'text-warning';
    return 'text-error';
  }

  if (!isBackendReachable.value) return 'text-error';
  if (latencyMs.value == null) return 'text-warning';
  if (latencyMs.value <= 90) return 'text-success';
  if (latencyMs.value <= 180) return 'text-warning';
  return 'text-error';
});

const latencyLabel = computed(() => {
  if (!isOnline.value) return 'offline';

  if (isLanMode) {
    const label = wifiLevel.value >= 3 ? 'Wi-Fi strong' : wifiLevel.value === 2 ? 'Wi-Fi medium' : 'Wi-Fi weak';
    if (latencyMs.value == null) return label;
    return `${label} | ${Math.round(latencyMs.value)} ms`;
  }

  if (!isBackendReachable.value) return 'timeout';
  if (latencyMs.value == null) return '-- ms';
  return `${Math.round(latencyMs.value)} ms`;
});

const onNetworkStateChange = () => {
  isOnline.value = navigator.onLine;
  wifiLevel.value = deriveWifiLevel();
};

const updateFromConnectionApi = () => {
  const info = connectionInfo();
  if (info?.rtt && info.rtt > 0) {
    latencyMs.value = info.rtt;
  }
  wifiLevel.value = deriveWifiLevel();
};

const measureBackendLatency = async () => {
  if (!isOnline.value || isMeasuring) {
    latencyMs.value = null;
    isBackendReachable.value = false;
    return;
  }

  isMeasuring = true;
  const started = performance.now();
  let timeoutId: ReturnType<typeof window.setTimeout> | null = null;

  try {
    await Promise.race([
      convexClient.mutation(api.rooms.getServerTime, {}),
      new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error('timeout')), 4000);
      }),
    ]);
    latencyMs.value = performance.now() - started;
    isBackendReachable.value = true;
  } catch {
    latencyMs.value = null;
    isBackendReachable.value = false;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    isMeasuring = false;
  }
};

const onConnectionChange: EventListener = () => {
  updateFromConnectionApi();
};

onMounted(() => {
  updateFromConnectionApi();

  if (isLanMode) {
    pingTimer = setInterval(() => {
      updateFromConnectionApi();
    }, 1000);
  } else {
    measureBackendLatency();
    pingTimer = setInterval(() => {
      measureBackendLatency();
    }, 2000);
  }

  window.addEventListener('online', onNetworkStateChange);
  window.addEventListener('offline', onNetworkStateChange);
  connectionInfo()?.addEventListener?.('change', onConnectionChange);
});

onUnmounted(() => {
  if (pingTimer) clearInterval(pingTimer);
  window.removeEventListener('online', onNetworkStateChange);
  window.removeEventListener('offline', onNetworkStateChange);
  connectionInfo()?.removeEventListener?.('change', onConnectionChange);
});
</script>

<template>
  <div class="connection-indicator" :class="qualityClass">
    <svg viewBox="0 0 24 24" class="wifi-icon" aria-hidden="true">
      <path
        d="M4 9a11 11 0 0 1 16 0"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        :class="{ dim: level < 3 }"
      />
      <path
        d="M7 12a7 7 0 0 1 10 0"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        :class="{ dim: level < 2 }"
      />
      <path
        d="M10 15a3 3 0 0 1 4 0"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        :class="{ dim: level < 1 }"
      />
      <circle cx="12" cy="18" r="1.2" fill="currentColor" :class="{ dim: level < 1 }" />
    </svg>
    <span class="latency">{{ latencyLabel }}</span>
  </div>
</template>

<style scoped>
.connection-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid var(--color-surface-hover);
  background: color-mix(in srgb, var(--color-secondary) 85%, transparent);
  backdrop-filter: blur(6px);
}

.wifi-icon {
  width: 16px;
  height: 16px;
}

.latency {
  font-size: 11px;
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  color: var(--color-text-primary);
}

.dim {
  opacity: 0.24;
}
</style>
